// ────────────────────────────────────────────────────────────
// CLAUDE SERVICE — calls the server-side proxy
// ────────────────────────────────────────────────────────────
// All deal-context assembly happens server-side now (in functions/api/claude/chat.ts).
// The client just sends { dealId, message, lens } and gets back text.

import { claudeApi } from './api';
import type {
  Deal, DealFile, TitleFinding, ChainLink, OwnershipPicture, Tract,
  ConversationMessage, Lens, PackageDraft, LeadEvent,
} from '../types';

export const claudeAvailable = (): boolean => true;
export const isMockMode = (): boolean => false;

export interface DealContextInput {
  deal: Deal;
  files: DealFile[];
  findings: TitleFinding[];
  chain: ChainLink[];
  picture?: OwnershipPicture;
  tracts: Tract[];
  packageDraft?: PackageDraft;
  recentLeadEvents: LeadEvent[];
  conversation: ConversationMessage[];
}

export async function chat(
  ctx: DealContextInput,
  userMessage: string,
  lens: Lens,
): Promise<{ text: string; mocked: boolean }> {
  const r = await claudeApi.chat(ctx.deal.id, userMessage, lens);
  return { text: r.text, mocked: false };
}


// Day 1 stubs — will get real implementations when Claude tools come online
export async function regenerateSection(
  ctx: DealContextInput,
  sectionName: string,
  instruction: string,
): Promise<{ text: string; mocked: boolean }> {
  // Build the same chat call but framed for section regen
  const message = `Regenerate the package section "${sectionName}" with this instruction: ${instruction}\n\nReturn only the new section body in markdown-lite. No preamble.`;
  const r = await claudeApi.chat(ctx.deal.id, message, 'package');
  return { text: r.text.trim(), mocked: false };
}

export async function regeneratePicture(
  ctx: DealContextInput,
): Promise<{ narrative: string; confidence: number; mocked: boolean }> {
  const message = `Regenerate the ownership picture from the current evidence. Return JSON only: {"narrative": "...", "confidence": <0-100 int>}.`;
  const r = await claudeApi.chat(ctx.deal.id, message, 'title');
  try {
    const cleaned = r.text.replace(/^```json\s*/i, '').replace(/\s*```$/, '').trim();
    const parsed = JSON.parse(cleaned);
    return { narrative: parsed.narrative, confidence: parsed.confidence, mocked: false };
  } catch {
    return { narrative: r.text, confidence: 50, mocked: false };
  }
}
